/**
 * WelcomeBackBanner — Phase 7e.
 *
 * Tiny affordance shown above the intake content when a returning
 * applicant has at least one previously-saved answer. The banner
 * names the section they're resuming on so the "where was I?" beat
 * is one glance instead of a scan through the nav. Dismissal is
 * in-memory: the banner reappears next session because every visit
 * is, by definition, a return visit.
 *
 * Visibility heuristics (engine-has-answers, not-on-review-leaf,
 * not-already-dismissed-this-session) are owned by App.tsx — the
 * component itself is dumb and renders unconditionally.
 *
 * Copy follows the spec in `phase-7e-plan.md`:
 *
 *   "👋 Welcome back, {first_name}. You're on \"{section}\" — picking
 *    up where you left off."
 *
 * The first-name segment is dropped when `firstName` is empty so we
 * don't dangle a comma in front of "You're on…".
 */
import styles from './WelcomeBackBanner.module.css';

export interface IWelcomeBackBannerProps {
  /**
   * Applicant's legal first name. Empty string when the field has not
   * yet been filled — the banner drops the personalised greeting in
   * that case rather than rendering "Welcome back, .".
   */
  firstName: string;
  /** Display label of the section the user is resuming on. */
  currentSectionLabel: string;
  /** Invoked when the user clicks "Got it". */
  onDismiss: () => void;
}

export function WelcomeBackBanner(
  props: IWelcomeBackBannerProps,
): JSX.Element {
  const { firstName, currentSectionLabel, onDismiss } = props;
  const trimmedName = firstName.trim();
  const greeting =
    trimmedName.length > 0 ? `Welcome back, ${trimmedName}` : 'Welcome back';
  return (
    // Reveal animation on mount. Wave 4 Slice 16 —
    // restructured to two-line layout: greeting on top (heavier weight),
    // resumption-context underneath as a quieter line. Less ambiguous
    // hierarchy than the previous single-line "greeting + you're on
    // 'X' — picking up..." format. Dismiss is now a ghost text-link
    // (one fewer bordered chip on the page).
    <div role="status" className={`${styles.banner} reveal_on_mount`}>
      <span className={styles.banner__icon} aria-hidden="true">
        👋
      </span>
      <div className={styles.banner__body}>
        <p className={styles.banner__greeting}>{greeting}</p>
        <p className={styles.banner__resume}>
          Picking up in{' '}
          <span className={styles.banner__section}>{currentSectionLabel}</span>
        </p>
      </div>
      <button
        type="button"
        className={styles.banner__dismiss}
        onClick={onDismiss}
      >
        Got it
      </button>
    </div>
  );
}
